import React from 'react';
import { useSelector } from 'react-redux';
import * as humps from 'humps';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import prodPathReplace from 'shared/prod-path-rewrite';
import { LEARNER_FINAL_SUMMARY_UPDATED_EVENT } from 'timelines/components/completion_criteria/portable-completion-panel/personalized-takeaways-access';
import {
  getLearnerFinalSummaryDetails,
  setLearnerFinalSummaryUpdated,
} from 'redux/actions/help-options';
import { RootState } from 'redux/schemas';
import {
  LearnerFinalSummaryHighlightSection,
  LearnerFinalSummaryStatus,
  LearnerFinalSummaryUpdatedEventData,
} from 'redux/schemas/models/help-options';
import { getCourseAliases } from 'redux/selectors/course';
import { getLearnerFinalSummary } from 'redux/selectors/help-options';
import { getCurrentUser } from 'redux/selectors/users';
import { useAppDispatch } from 'redux/store';
import pusherService from 'shared/services/pusher-service';
import useScrollTop from 'shared/hooks/use-scroll';
import {
  screenMdMax,
} from 'styles/global_defaults/media-queries';
import {
  PERSONALIZED_TAKEAWAYS_SCROLL_EVENT,
  PersonalizedTakeawaysSectionId,
} from './personalized-takeaways-sections';
import fullPageStyles from './personalized-takeaways-page.styles';

const cardMetrics = [
  { labelKey: 'ASSIGNMENT_PRACTICE', valueKey: 'submissions' },
  { labelKey: 'SOCIAL_ACTIVITIES', valueKey: 'discussions' },
  { labelKey: 'FEEDBACK_ACTIVITIES', valueKey: 'rolePlays' },
  { labelKey: 'QUIZZES_AND_SURVEYS', valueKey: 'quizzes' },
  { labelKey: 'EVENTS', valueKey: 'eventsAttended' },
] as const;

const emptyContentTranslationKeys = [
  'AT_A_GLANCE',
  'IN_THE_NUMBERS',
  'COURSE_HIGHLIGHTS',
  'YOUR_STRENGTHS',
  'YOUR_NEXT_LEVEL',
  'KEEP_LEARNING',
  'YOU_DID_IT',
  'DISCLAIMER',
] as const;

type EmptyContentTranslationKey = typeof emptyContentTranslationKeys[number];

const isEmptyContentTranslationKey = (key: string): key is EmptyContentTranslationKey => (
  emptyContentTranslationKeys.includes(key as EmptyContentTranslationKey)
);

const getEmptyContentTranslation = (emptyContentI18nKey?: string) => {
  const [sectionKey, contentKey] = emptyContentI18nKey?.split('.') || [];

  if (contentKey !== 'empty_content' || !sectionKey) {
    return '';
  }

  const translationKey = sectionKey.toUpperCase();

  if (!isEmptyContentTranslationKey(translationKey)) {
    return '';
  }

  return t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.EMPTY_CONTENT[translationKey]();
};

const getSectionText = (section?: LearnerFinalSummaryHighlightSection) => {
  if (!section) {
    return {
      headline: '', summary: '', intro: '', bullets: [], resources: [], emptyMessage: '',
    };
  }

  const content = section.sectionPayload?.content;
  const headline = typeof content?.headline === 'string'
    ? content.headline.trim()
    : '';
  const summary = typeof content?.summary === 'string'
    ? content.summary.trim()
    : '';
  const intro = typeof content?.intro === 'string' ? content.intro.trim() : '';
  const bullets = Array.isArray(content?.bullets)
    ? content.bullets.filter((bullet): bullet is string => typeof bullet === 'string' && !!bullet.trim())
    : [];
  const resources = Array.isArray(content?.resources) ? content.resources : [];
  const emptyContentI18nKey = section.sectionPayload?.emptyContentI18nKey;
  const emptyMessage = getEmptyContentTranslation(emptyContentI18nKey);

  return {
    headline, summary, intro, bullets, resources, emptyMessage,
  };
};

const renderSectionContent = (section?: LearnerFinalSummaryHighlightSection) => {
  const {
    headline, summary, intro, bullets, resources, emptyMessage,
  } = getSectionText(section);

  if (headline || summary || intro || bullets.length || resources.length) {
    return (
      <div className='section-copy'>
        {headline && <p className='section-headline text-large-body'>{headline}</p>}
        {summary && <p className='section-summary text-regular mb-0'>{summary}</p>}
        {intro && <p className='section-intro text-regular'>{intro}</p>}
        {!!bullets.length && (
          <ul className='section-list text-regular'>
            {bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
          </ul>
        )}
        {!!resources.length && (
          <ul className='section-list resource-list text-regular'>
            {resources.map((resource) => (
              <li key={`${resource.resourceType}-${resource.lecturePageId}-${resource.componentId}-${resource.resourceId}-${resource.reason}`}>
                {resource.title && <strong className='resource-title'>{resource.title}</strong>}
                {resource.reason && <span>{resource.reason}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (emptyMessage) {
    return <p className='section-empty text-large-body mb-0'>{emptyMessage}</p>;
  }

  return null;
};

const getMetricValue = (
  section: LearnerFinalSummaryHighlightSection | undefined,
  valueKey: typeof cardMetrics[number]['valueKey'],
) => {
  const value = section?.sectionPayload?.[valueKey];
  return typeof value === 'number' ? value : null;
};

type LoadingSectionProps = {
  id: PersonalizedTakeawaysSectionId;
  kicker: string;
  title: string;
};

const LoadingSection = ({ id, kicker, title }: LoadingSectionProps) => (
  <section id={id} className='section'>
    <div className='divider' />
    <h2 className='section-kicker course-title-xs mb-0'>{kicker}</h2>
    <h3 className='section-title course-title-l mt-0'>{title}</h3>
    <div className='generating-block'>
      <GradientAiIcon icon='gen-ai' size='medium' />
      <div className='generating-label text-small semi-bold'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.GENERATING()}</div>
    </div>
  </section>
);

type SummarySectionProps = {
  id: PersonalizedTakeawaysSectionId;
  kicker: string;
  title: string;
  summarySection?: LearnerFinalSummaryHighlightSection;
};

const SummarySection = ({ id, kicker, title, summarySection }: SummarySectionProps) => {
  if (!summarySection) {
    return <LoadingSection id={id} kicker={kicker} title={title} />;
  }

  return (
    <section id={id} className='section'>
      <div className='divider' />
      <h2 className='section-kicker course-title-xs mb-0'>{kicker}</h2>
      <h3 className='section-title course-title-l mt-0'>{title}</h3>
      {renderSectionContent(summarySection)}
    </section>
  );
};

const PersonalizedTakeawaysPage = () => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state, $rootScope] = injectServices(['$state', '$rootScope']);
  const dispatch = useAppDispatch();
  const requestedSummaryIdRef = React.useRef<number | null>(null);
  const [hasAttemptedInitialHighlightsLoad, setHasAttemptedInitialHighlightsLoad] = React.useState(false);
  const mainGridCtrl = $rootScope?.$$childHead?.MainGridCtrl;
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);
  const courseId = useSelector((state: RootState) => state.app.currentCourseId);
  const aliases = useSelector((state: RootState) => getCourseAliases(state));
  const courseAliases = aliases?.courseAliases || {};
  const currentUser = useSelector(getCurrentUser);
  const fromState = $state.params?.from;
  const learnerFinalSummaryReportId = $state.params?.learnerFinalSummaryReportId;
  const hasLearnerFinalSummaryReportId = !!learnerFinalSummaryReportId;
  const learnerFinalSummary = useSelector((state: RootState) => getLearnerFinalSummary(state, currentUser?.id));
  const learnerFinalSummaryStatus = learnerFinalSummary?.status;
  const highlightSections = React.useMemo<LearnerFinalSummaryHighlightSection[]>(
    () => (hasLearnerFinalSummaryReportId ? (learnerFinalSummary?.highlightSections || []) : []),
    [hasLearnerFinalSummaryReportId, learnerFinalSummary?.highlightSections],
  );
  const hasLoadedHighlights = highlightSections.length > 0;
  const sectionsByType = React.useMemo<Record<string, LearnerFinalSummaryHighlightSection>>(() => {
    const nextSections: Record<string, LearnerFinalSummaryHighlightSection> = {};

    highlightSections.forEach((section: LearnerFinalSummaryHighlightSection) => {
      nextSections[humps.camelize(section.sectionType)] = section;
    });

    return nextSections;
  }, [highlightSections]);
  const isScrolled = useScrollTop(document.getElementById('main-panel') || document.body);

  React.useEffect(() => {
    requestedSummaryIdRef.current = null;
    setHasAttemptedInitialHighlightsLoad(false);
  }, [learnerFinalSummaryReportId]);

  React.useEffect(() => {
    if (!catalogId || !currentUser?.id || !learnerFinalSummaryReportId || hasLoadedHighlights) {
      if (hasLoadedHighlights && !hasAttemptedInitialHighlightsLoad) {
        setHasAttemptedInitialHighlightsLoad(true);
      }
      return () => {};
    }

    if (requestedSummaryIdRef.current === learnerFinalSummaryReportId) {
      return () => {};
    }

    requestedSummaryIdRef.current = learnerFinalSummaryReportId;

    const requestPromise = dispatch(getLearnerFinalSummaryDetails({
      catalogId,
      userId: currentUser.id,
      learnerFinalSummaryReportId,
    }));

    requestPromise.finally(() => {
      setHasAttemptedInitialHighlightsLoad(true);
    });

    return () => {
      requestPromise.abort();
    };
  }, [
    catalogId,
    currentUser?.id,
    dispatch,
    hasAttemptedInitialHighlightsLoad,
    hasLoadedHighlights,
    learnerFinalSummaryReportId,
  ]);

  React.useEffect(() => {
    if (
      !courseId
      || !catalogId
      || !currentUser?.id
      || !learnerFinalSummaryReportId
      || !hasAttemptedInitialHighlightsLoad
      || hasLoadedHighlights
    ) {
      return () => {};
    }

    const pusherChannel = pusherService.initializeCourseChannel(courseId);
    if (!pusherChannel) {
      return () => {};
    }

    const handleLearnerFinalSummaryUpdated = (pusherData: unknown) => {
      const data = humps.camelizeKeys(pusherData as Record<string, unknown>) as LearnerFinalSummaryUpdatedEventData;

      if (data.userId !== currentUser.id || data.learnerFinalSummaryReportId !== learnerFinalSummaryReportId) {
        return;
      }

      dispatch(setLearnerFinalSummaryUpdated({
        learnerFinalSummaryReportId: data.learnerFinalSummaryReportId,
        userId: currentUser.id,
        status: data.status,
        available: data.available,
        generatedAt: data.generatedAt,
        errorMessage: data.errorMessage,
      }));

      if (data.status === LearnerFinalSummaryStatus.COMPLETED && data.available) {
        dispatch(getLearnerFinalSummaryDetails({
          catalogId,
          userId: currentUser.id,
          learnerFinalSummaryReportId: data.learnerFinalSummaryReportId,
        }));
      }
    };

    pusherChannel.bind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleLearnerFinalSummaryUpdated);

    return () => {
      pusherChannel.unbind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleLearnerFinalSummaryUpdated);
    };
  }, [
    catalogId,
    courseId,
    currentUser?.id,
    dispatch,
    hasAttemptedInitialHighlightsLoad,
    hasLoadedHighlights,
    learnerFinalSummaryReportId,
    learnerFinalSummaryStatus,
  ]);

  const atAGlanceSection = sectionsByType.atAGlance;
  const inTheNumbersSection = sectionsByType.inTheNumbers;
  const courseHighlightsSection = sectionsByType.courseHighlights;
  const yourStrengthsSection = sectionsByType.yourStrengths;
  const yourNextLevelSection = sectionsByType.yourNextLevel;
  const keepLearningSection = sectionsByType.keepLearning;
  const disclaimerHighlight = hasLearnerFinalSummaryReportId ? sectionsByType.disclaimer : undefined;
  const youDidItSection = sectionsByType.youDidIt;
  const { headline: disclaimerHeadline, summary: disclaimerSummary } = getSectionText(disclaimerHighlight);
  const disclaimerText = disclaimerSummary || disclaimerHeadline;
  const inTheNumbersEmptyMessage = getEmptyContentTranslation(
    inTheNumbersSection?.sectionPayload?.emptyContentI18nKey,
  );
  const hasInTheNumbersMetrics = cardMetrics.some(
    ({ valueKey }) => getMetricValue(inTheNumbersSection, valueKey) !== null,
  );
  const youDidItContent = renderSectionContent(youDidItSection);
  let inTheNumbersContent;

  if (!inTheNumbersSection) {
    inTheNumbersContent = (
      <div className='stats-row'>
        {cardMetrics.map(({ labelKey }) => (
          <div key={labelKey} className='stats-card'>
            <div className='mini-spinner' />
            <div className='stat-label text-medium semi-bold'>
              {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE[labelKey]()}
            </div>
          </div>
        ))}
      </div>
    );
  } else if (hasInTheNumbersMetrics) {
    inTheNumbersContent = (
      <div className='stats-row'>
        {cardMetrics.map(({ labelKey, valueKey }) => (
          <div key={labelKey} className='stats-card'>
            <div className='stat-value course-title-l font-weight-bold'>
              {getMetricValue(inTheNumbersSection, valueKey) ?? 0}
            </div>
            <div className='stat-label text-medium semi-bold'>
              {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE[labelKey]()}
            </div>
          </div>
        ))}
      </div>
    );
  } else {
    inTheNumbersContent = (
      <p className='section-empty text-large-body mb-0'>{inTheNumbersEmptyMessage}</p>
    );
  }

  React.useEffect(() => {
    const onScrollSection = (event: CustomEvent<{ sectionId?: PersonalizedTakeawaysSectionId }>) => {
      const targetId = event?.detail?.sectionId;

      if (!targetId) {
        return;
      }

      document.getElementById(targetId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };

    const listener = onScrollSection as unknown as EventListener;
    window.addEventListener(PERSONALIZED_TAKEAWAYS_SCROLL_EVENT, listener);

    return () => {
      window.removeEventListener(PERSONALIZED_TAKEAWAYS_SCROLL_EVENT, listener);
    };
  }, []);

  React.useEffect(() => {
    const syncLhsMode = () => {
      if (mainGridCtrl?.shouldHideLHS?.()) {
        return;
      }

      const isLarge = window.innerWidth > screenMdMax;

      // Collapse desktop LHS for medium *and* small screens; desktop nav isn't shown there.
      if (!isLarge) {
        if (mainGridCtrl?.isLhsExpandedDesktop) {
          mainGridCtrl.isLhsExpandedDesktop = false;
          mainGridCtrl.toggleLhsDesktop?.();
        }
        return;
      }

      if (mainGridCtrl && !mainGridCtrl.isLhsExpandedDesktop) {
        mainGridCtrl.isLhsExpandedDesktop = true;
        mainGridCtrl.toggleLhsDesktop?.();
      }
    };

    syncLhsMode();
    window.addEventListener('resize', syncLhsMode);

    return () => {
      window.removeEventListener('resize', syncLhsMode);
    };
  }, [mainGridCtrl]);

  const onBackToCourse = () => {
    if (!catalogId) {
      return;
    }

    if (fromState === 'learner-progress') {
      $state.go('learner-progress', { catalogId }, { replace: true });
      return;
    }

    $state.go('course-home', { catalogId });
  };

  const onOpenMobileMenu = () => {
    mainGridCtrl?.toggleLhsMobile?.();
  };

  return (
    <main css={fullPageStyles}>
      <div className={`mobile-burger-bar ${isScrolled ? 'scrolled' : ''}`}>
        <button
          type='button'
          className='mobile-burger-button'
          aria-label={t.LHS.OPEN_LHS()}
          onClick={onOpenMobileMenu}
        >
          <NvIcon icon='menu' size='small' />
        </button>
      </div>
      <div className='takeaways-content'>
        <div className='takeaways-spark'>
          <GradientAiIcon icon='gen-ai' size='large' />
        </div>
        <h1 className='eyebrow course-title-xs mt-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.HEADER()}</h1>
        <div className='main-title header-xl font-weight-bold'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.TITLE()}</div>

        <section id={PersonalizedTakeawaysSectionId.AT_A_GLANCE} className='section'>
          <div className='divider' />
          <h2 className='section-kicker course-title-xs mb-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.AT_A_GLANCE()}</h2>
          {atAGlanceSection ? renderSectionContent(atAGlanceSection) : (
            <div className='generating-block'>
              <GradientAiIcon icon='gen-ai' size='medium' />
              <div className='generating-label text-small semi-bold'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.GENERATING()}</div>
            </div>
          )}
        </section>

        <section id={PersonalizedTakeawaysSectionId.IN_THE_NUMBERS} className='section'>
          <div className='divider' />
          <h2 className='section-kicker course-title-xs mb-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.IN_THE_NUMBERS()}</h2>
          <h3 className='section-title course-title-l mt-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.HOW_YOU_SHOWED_UP()}</h3>
          {inTheNumbersContent}
        </section>

        <SummarySection
          id={PersonalizedTakeawaysSectionId.COURSE_HIGHLIGHTS}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.COURSE_HIGHLIGHTS(courseAliases)}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.WHAT_MATTERED_MOST()}
          summarySection={courseHighlightsSection}
        />
        <SummarySection
          id={PersonalizedTakeawaysSectionId.YOUR_STRENGTHS}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOUR_STRENGTHS()}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.WHERE_YOU_EXCELLED()}
          summarySection={yourStrengthsSection}
        />
        <SummarySection
          id={PersonalizedTakeawaysSectionId.YOUR_NEXT_LEVEL}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOUR_NEXT_LEVEL()}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.WHERE_YOU_CAN_GROW()}
          summarySection={yourNextLevelSection}
        />
        <SummarySection
          id={PersonalizedTakeawaysSectionId.KEEP_LEARNING}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.KEEP_LEARNING()}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.MOMENTS_WORTH_REVISITING()}
          summarySection={keepLearningSection}
        />

        <section id={PersonalizedTakeawaysSectionId.CONGRATULATIONS} className='section completion-copy mb-0'>
          <div className='completion-badge'>
            <img
              className='congrats-image'
              src={prodPathReplace('images/congratulations.png')}
              alt=''
            />
          </div>
          <h2 className='section-kicker course-title-xs mb-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOU_DID_IT(currentUser?.firstName ?? '')}</h2>
          <h3 className='section-title course-title-l mt-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.CONGRATULATIONS()}</h3>
          {youDidItContent || (
            <p className='completion-body text-large-body'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.COMPLETION_MESSAGE(courseAliases)}</p>
          )}
          <button type='button' className='back-button text-medium semi-bold' onClick={onBackToCourse}>
            {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.BACK_TO_COURSE(courseAliases)}
          </button>
          <p className='note text-small semi-bold mb-0'>
            {disclaimerText || t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.NOTE()}
          </p>
        </section>
      </div>
    </main>
  );
};

export default PersonalizedTakeawaysPage;
